import './utils/sentryInstrumentation';
import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { applyDocumentMeta } from './branding/applyDocumentMeta';
import { BrandingProvider } from './branding/BrandingProvider';
import RouteErrorScreen from './components/RouteErrorScreen/RouteErrorScreen';
import { resolvePartner } from './config';
import { GlobalStyle } from './containers/App';
import AppRoutes from './containers/Routes/AppRouterProvider';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import store from './reduxFolder/configureStore';
import { buildTheme } from './styles';
import { darkColors, lightColors } from './styles/themes';
import i18nConfig from './translations/i18nConfig';
import { installDomTranslationGuard } from './utils/domTranslationGuard';

// Resolve the partner once at module load — before any React mounting — so
// that Sentry tags are in place for events captured during initial render.
// resolvePartner runs zod validation; a corrupt config crashes the bundle
// here rather than rendering a broken page.
const partnerConfig = resolvePartner(window.location.hostname);
Sentry.setTag('partnerId', partnerConfig.partnerId);
Object.entries(partnerConfig.sentry.tags).forEach(([k, v]) =>
  Sentry.setTag(k, v),
);
applyDocumentMeta(partnerConfig);
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.info(`[branding] partner=${partnerConfig.partnerId}`);
}

// Partner theme overrides merged on top of the base palette. Built once at
// boot — themes don't change at runtime within a session.
const lightTheme = buildTheme(lightColors, partnerConfig.theme.light);
const darkTheme = buildTheme(darkColors, partnerConfig.theme.dark);

const container = document.getElementById('root');

// Anything thrown while rendering the route tree (a component bug, or a chunk
// still failing after lazyWithRetry) renders RouteErrorScreen instead of
// react-router's built-in developer page. The router wraps the root route in
// its own error boundary, so this is the place to catch render errors — a
// boundary around <RouterProvider> would never see them.
const router = createBrowserRouter([
  { path: '*', element: <AppRoutes />, errorElement: <RouteErrorScreen /> },
]);

const ThemedApp = () => {
  const { resolvedTheme } = useThemeMode();
  const theme = resolvedTheme === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      {/* Above the router so the base styles (font, body colors, button reset)
          stay mounted while the router shows RouteErrorScreen in place of the
          route tree. */}
      <GlobalStyle />
      <I18nextProvider i18n={i18nConfig}>
        <RouterProvider router={router} />
      </I18nextProvider>
    </ThemeProvider>
  );
};

const app = (
  <Provider store={store}>
    <BrandingProvider config={partnerConfig}>
      <ThemeModeProvider>
        <DemoModeProvider>
          <ThemedApp />
        </DemoModeProvider>
      </ThemeModeProvider>
    </BrandingProvider>
  </Provider>
);

// Browser page translation (Chrome, Yandex, Safari) rewrites text nodes React
// still references; without this React crashes the route with NotFoundError.
installDomTranslationGuard();

const root = createRoot(container);
root.render(app);
